/**
 * Toast de la maquette (`.toast` : encre, texte papier, icône d'état) pour TOUS les toasts rendus sous la v2 :
 * options par défaut du ChakraProvider de mountV2 (toastOptions.defaultOptions), donc aussi les toasts des écrans v1
 * réutilisés (panier : carte cadeau invalide, paiement refusé, session expirée…), sans modifier leur code. Un toast qui
 * passe son propre `render` le garde.
 *
 * Revue du 04/10 : les toasts Chakra par défaut (vert #38A169 3,25:1, rouge #E53E3E 4,13:1, sous l'AA ; en bas, au
 * centre ; police de la v1) recouvraient la barre « Payer » à 390 et ne ressemblaient pas au toast « C’est réservé ».
 * Position : en haut à droite, 16 px sous le bas RÉEL de l'en-tête du site au moment du toast (bandeau promo compris :
 * --m-header-h n'en tient pas compte, le toast recouvrait l'en-tête de 8 à 18 px).
 *
 * Rendu dans le portail de Chakra, hors de #mindbody-app : peint avec les jetons du thème Chakra v2 (`mmv2.*`).
 *
 * Revue du 05/10 :
 * - pas de rôle sur le contenu : la coque de Chakra (div.chakra-toast__inner) porte déjà role="status" aria-atomic,
 *   dans la région aria-live="polite" du gestionnaire ; régions vives imbriquées = annonce en double possible. Une
 *   erreur passe la coque en role="alert" ;
 * - sans statut : information (icône info, comme le défaut de Chakra), plus la coche verte de succès ;
 * - largeur fixe (360 px, l'écran moins 32 px) : les toasts empilés n'ont plus des bords en escalier ; sous 640 px,
 *   pleine largeur à 12 px des bords (maquette .toasts) ;
 * - pile au-dessus des calques de paiement de la v1 (z-index 9 999 999, theme/components/Modal.ts), sous lesquels
 *   le toast d'erreur restait flou et illisible.
 */
import { Box, Flex, type ToastProps } from '@chakra-ui/react';
import { useLayoutEffect, useRef } from 'react';
import { toastTopOffset } from './toastOffset';
import { typo } from './typo';

const PATHS = {
  success: 'M20 6 9 17l-5-5',
  error: 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4.5M12 17.2v.1',
  info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 16.5V11M12 7.6v.1',
  close: 'M18 6 6 18M6 6l12 12',
} as const;

function Glyph({ path, size = 17 }: { path: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={path} />
    </svg>
  );
}

type Status = 'success' | 'error' | 'warning' | 'info' | 'loading';

const iconOf = (status?: Status) =>
  status === 'error' || status === 'warning' ? PATHS.error : status === 'success' ? PATHS.success : PATHS.info;
/** Couleur de l'icône sur l'encre : teintes claires des états (vert, rouge), papier pour une information. */
const iconColor = (status?: Status) =>
  status === 'error' || status === 'warning' ? 'mmv2.red50' : status === 'success' ? 'mmv2.green50' : 'mmv2.paper';

/** Au-dessus des fenêtres modales du thème Chakra v1 (theme/components/Modal.ts : 9 999 999). */
export const V2_TOAST_Z_INDEX = 10000000;
/** Sous cette largeur d'écran, toasts pleine largeur à 12 px des bords (maquette, @media (max-width: 640px)). */
const NARROW = '(max-width: 640px)';

export function V2Toast({
  title,
  description,
  status,
  isClosable,
  onClose,
}: Pick<ToastProps, 'title' | 'description' | 'isClosable' | 'onClose'> & { status?: Status }) {
  const ref = useRef<HTMLDivElement>(null);
  /*
   * Toast d'erreur sans titre ni description (réponse 403 sans message, calque d'échec Alma en local) : une icône et une
   * croix seules (revue du 05/10). Repli générique ; textes venus de la v1 ou du serveur passés par typo() (’, « : »).
   */
  const shownTitle = typeof title === 'string' ? typo(title).trim() : title;
  const shownDescription = typeof description === 'string' ? typo(description).trim() : description;
  const fallback =
    !shownTitle && !shownDescription
      ? status === 'error' || status === 'warning'
        ? 'Une erreur est survenue. Réessayez dans un instant.'
        : null
      : null;
  // La pile de toasts (région fixe de Chakra, en haut à droite) descend sous l'en-tête du site à l'apparition de chaque
  // toast, et garde 16 px au bord droit (8 px de la pile + 8 px de marge de chaque toast).
  useLayoutEffect(() => {
    if (status === 'error') ref.current?.closest('.chakra-toast__inner')?.setAttribute('role', 'alert');
    const list = ref.current?.closest<HTMLElement>('[id^="chakra-toast-manager-"]');
    if (!list || !list.id.endsWith('top-right')) return;
    const narrow = typeof window.matchMedia === 'function' && window.matchMedia(NARROW).matches;
    list.style.top = `${Math.max(0, toastTopOffset() - 8)}px`;
    // 8 px de marge de chaque toast (Chakra) : 16 px du bord, ou 12 px sous 640 px.
    list.style.right = narrow ? '4px' : '8px';
    list.style.zIndex = String(V2_TOAST_Z_INDEX);
  }, [status]);
  return (
    <Flex
      ref={ref}
      className="mmv2-toast"
      data-status={status ?? 'info'}
      align="flex-start"
      gap="12px"
      p="14px 16px"
      borderRadius="mmv2Card"
      bg="mmv2.ink"
      color="mmv2.paper"
      boxShadow="mmv2Lg"
      fontFamily="mmv2"
      fontSize="13.5px"
      // 19 px : demi-interligne entière (texte centré au pixel) ; icône de 17 px centrée sur la 1re ligne (marge 1 px).
      lineHeight="19px"
      w="min(360px, calc(100vw - 32px))"
      sx={{ [`@media ${NARROW}`]: { width: 'calc(100vw - 24px)' } }}
      textAlign="left"
    >
      <Box color={iconColor(status)} flex="0 0 auto" mt="1px">
        <Glyph path={iconOf(status)} />
      </Box>
      {/* Jamais un mot seul en dernière ligne (« place. », revue du 05/10). */}
      <Box flex="1 1 auto" minW={0} sx={{ textWrap: 'pretty' }}>
        {shownTitle || fallback ? (
          <Box as="p" m={0} fontWeight={shownDescription ? 600 : 500}>
            {shownTitle || fallback}
          </Box>
        ) : null}
        {shownDescription ? (
          <Box as="p" m={shownTitle ? '2px 0 0' : 0} color="mmv2.paper" opacity={0.86}>
            {shownDescription}
          </Box>
        ) : null}
      </Box>
      {isClosable ? (
        <Box
          as="button"
          type="button"
          aria-label="Fermer"
          onClick={onClose}
          flex="0 0 auto"
          display="grid"
          placeItems="center"
          w="28px"
          h="28px"
          // Croix centrée sur la 1re ligne (19 px) : (19 − 28) / 2.
          mt="-4.5px"
          mr="-6px"
          p={0}
          border={0}
          borderRadius="999px"
          bg="transparent"
          color="mmv2.paper"
          cursor="pointer"
          _hover={{ bg: 'whiteAlpha.200' }}
          _focusVisible={{ outline: '2px solid', outlineColor: 'mmv2.paper', outlineOffset: '1px' }}
        >
          <Glyph path={PATHS.close} size={15} />
        </Box>
      ) : null}
    </Flex>
  );
}
